/**
 * CollapsibleSection Component
 *
 * Reusable collapsible section with consistent styling.
 * Used by ResponsePreview, StatusTrackingSection, etc.
 *
 * E029-S020: 8.20.7 - Layered Help System
 * - Removed tooltip icons in favor of subtitles (Layer 1: Inline Hints)
 * - Subtitles provide always-visible context without cluttering the UI
 *
 * sl-help-decentralized.2: Added (i) icon for section-specific coachmarks
 * - Optional onInfoClick prop triggers coachmark
 * - OCP: Extended without modifying existing behavior
 */

import { InformationIcon } from '@blocksuite/icons/rc';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import * as panelStyles from '../automation-panel.css';
import * as styles from './collapsible-section.css';

interface CollapsibleSectionProps {
  /** Section title */
  title: string;
  /** Optional subtitle shown below title (replaces tooltip) */
  subtitle?: string;
  /** Optional badge to show on the right */
  badge?: {
    label: string;
    status: 'active' | 'incomplete' | 'info';
  };
  /** Start collapsed (default: false) */
  defaultCollapsed?: boolean;
  /**
   * Optional localStorage key for persisting the open/closed state across
   * page reloads. When set, the initial state is read from localStorage
   * (falling back to !defaultCollapsed) and writes propagate on toggle.
   */
  persistKey?: string;
  /** Callback when (i) icon is clicked - shows section coachmark */
  onInfoClick?: () => void;
  /** Optional testid forwarded to the outer wrapper for E2E hooks */
  'data-testid'?: string;
  /** Optional stable role hook forwarded to the outer wrapper */
  'data-sl-role'?: string;
  /** Optional stable id for the content body */
  contentId?: string;
  /** Section content */
  children: ReactNode;
}

function readPersistedOpen(key: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* SSR / sandboxed */
  }
  return fallback;
}

/**
 * SubSection - for nested sections within a CollapsibleSection
 * Non-collapsible, simpler header style
 */
interface SubSectionProps {
  /** Section title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Optional badge */
  badge?: {
    label: string;
    status: 'active' | 'incomplete' | 'info';
  };
  /** Section content */
  children: ReactNode;
}

export const SubSection: FC<SubSectionProps> = ({
  title,
  subtitle,
  badge,
  children,
}) => {
  return (
    <div className={styles.subSection}>
      <div className={styles.subSectionHeader}>
        <div>
          <div className={styles.subSectionTitle}>{title}</div>
          {subtitle && (
            <div className={styles.subSectionSubtitle}>{subtitle}</div>
          )}
        </div>
        {badge && (
          <span className={styles.badge} data-status={badge.status}>
            {badge.label}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};

/** Divider between sub-sections */
export const SubSectionDivider: FC = () => (
  <div className={styles.subSectionDivider} />
);

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  title,
  subtitle,
  badge,
  defaultCollapsed = false,
  persistKey,
  onInfoClick,
  'data-testid': testId,
  'data-sl-role': slRole,
  contentId,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(() =>
    persistKey
      ? readPersistedOpen(persistKey, !defaultCollapsed)
      : !defaultCollapsed
  );

  useEffect(() => {
    if (!persistKey) return;
    try {
      window.localStorage.setItem(persistKey, String(isExpanded));
    } catch {
      /* SSR / sandboxed */
    }
  }, [persistKey, isExpanded]);

  return (
    <div
      className={`${panelStyles.section} ${styles.section}`}
      data-testid={testId}
      data-sl-role={slRole}
      data-sl-state={isExpanded ? 'expanded' : 'collapsed'}
    >
      <div className={styles.sectionHeader}>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.headerToggle}
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          <div className={styles.headerLeft}>
            <div className={styles.titleWrapper}>
              <span
                className={panelStyles.sectionTitle}
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span className={styles.chevron} aria-hidden="true">
                  ▶
                </span>
                {title}
              </span>
              {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            </div>
          </div>
        </button>
        {onInfoClick && (
          <button
            type="button"
            className={styles.infoButton}
            onClick={() => {
              if (!isExpanded) {
                setIsExpanded(true);
              }
              setTimeout(() => onInfoClick(), 50);
            }}
            aria-label={`Help for ${title}`}
          >
            <InformationIcon />
          </button>
        )}
        {badge && (
          <span className={styles.badge} data-status={badge.status}>
            {badge.label}
          </span>
        )}
      </div>

      {/* Collapsible Content */}
      <div
        id={contentId}
        className={styles.contentFrame}
        aria-hidden={!isExpanded}
        inert={isExpanded ? undefined : true}
      >
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};
